import { type ReactElement } from 'react'
import { confirmDelete } from '@util'
import { type UnsavedProgram } from '@terros/common'
import { useNavigate, useRouter, createFileRoute } from '@tanstack/react-router'
import { showNotification } from '@mantine/notifications'
import { useForm } from '@mantine/form'
import { useToastError } from '@hooks'
import { useRemoveProgram, useTranslation, useUpdateProgram } from '@front/model'
import { ButtonDelete, ButtonSave, DangerZone, NotReady, Page } from '@components'
import { ProgramHeading } from '../program_.$programId/ProgramHeader'
import { useProgramContext } from '../program_.$programId/context'
import { ProgramPageContainer } from '../program/ProgramPageContainer'
import { ProgramForm } from './ProgramForm'

export const ProgramEditRoute = (): ReactElement => {
  const { program, refetch, isFetching } = useProgramContext()

  if (isFetching) {
    return <NotReady isLoading={isFetching} />
  }

  return <ProgramEdit />
}

const ProgramEdit = (): ReactElement => {
  const { t } = useTranslation()
  const { program, refetch, isFetching } = useProgramContext()
  const { update, isUpdating, error } = useUpdateProgram()
  const { remove, isRemoving, error: removeError } = useRemoveProgram()
  const navigate = useNavigate()
  const router = useRouter()
  useToastError(error)
  useToastError(removeError)
  const form = useForm<UnsavedProgram>({
    initialValues: {
      ...program,
      name: program.name === 'Untitled Program' ? '' : program.name,
      completion: program.completion ?? 'timeline',
    },
  })

  const handleSubmit = async (values: UnsavedProgram): Promise<void> => {
    await update({ ...program, ...values })
    form.resetDirty(values)
  }

  const handleMoveToTrash = async (): Promise<void> => {
    const confirmed = await confirmDelete({
      title: t('Move Program to Trash'),
      message: t('You can restore {{name}} anytime from the Trash.', { name: program.name }),
      label: t('Move to Trash'),
      t,
    })
    if (!confirmed) return

    await remove({ programId: program.programId })
    form.resetDirty()
    router.history.back()
    showNotification({
      title: t('Program Moved to Trash'),
      message: t('You can restore {{name}} anytime from the Trash.', { name: program.name }),
      color: 'green',
    })
  }

  return (
    <form className='w-full' onSubmit={form.onSubmit(handleSubmit)}>
      <ProgramPageContainer>
        <ProgramHeading isFetching={isFetching} refetch={refetch} title='Program'>
          <ButtonSave disabled={isUpdating || !form.isDirty()} loading={isUpdating} type='submit' />
        </ProgramHeading>
        <Page.Content scrollArea>
          <ProgramForm form={form} showStatus />
          {!program.isDeleted && (
            <DangerZone>
              <ButtonDelete loading={isRemoving} onClick={handleMoveToTrash}>
                {t('Move Program to Trash')}
              </ButtonDelete>
            </DangerZone>
          )}
        </Page.Content>
      </ProgramPageContainer>
    </form>
  )
}

export const Route = createFileRoute('/program_/$programId/setup/')({
  component: ProgramEditRoute,
})
